import { useCallback, useEffect, useState } from "react";
import { Buildings, Plus, Wrench, Key } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function InhouseListPanel({ userRole = "admin" }) {
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [workshops, setWorkshops] = useState([]);
  const [wForm, setWForm] = useState({ name: "", email: "", phone: "" });
  const [wBusy, setWBusy] = useState(false);
  const canReset = ["ceo", "coceo", "dev"].includes(userRole);

  const refresh = useCallback(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addCompany = async () => {
    if (!newCompany.trim()) return;
    setBusy(true);
    try {
      await api.post("/companies", { name: newCompany.trim() });
      toast.success("Company added to the Inhouse List");
      setNewCompany("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createWorkshop = async (e) => {
    e.preventDefault();
    setWBusy(true);
    try {
      await api.post("/workshops", wForm);
      toast.success("Workshop account created — login credentials emailed to the partner");
      setWForm({ name: "", email: "", phone: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWBusy(false);
    }
  };

  const resetWorkshopPw = async (w) => {
    if (!window.confirm(`Reset password for ${w.name}? A new temporary password will be emailed to ${w.email}.`)) return;
    try {
      await api.post(`/workshops/${w.id}/reset-password`);
      toast.success("New temporary password emailed to the workshop");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-10" data-testid="inhouse-list-panel">
      <div className="space-y-5">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
            <Buildings size={24} className="text-primary" /> Inhouse List
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Partner companies & workshops for in-house towing referrals. This list feeds the referral form and workshop partner accounts.
          </p>
        </div>
        <div className="flex gap-2">
          <Input data-testid="inhouse-add-input" value={newCompany} onChange={(e) => setNewCompany(e.target.value)}
            placeholder="Register new in-house collaboration company..."
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCompany())}
            className="max-w-md rounded-sm border-input bg-card focus-visible:ring-primary" />
          <Button data-testid="inhouse-add-button" onClick={addCompany} disabled={busy}
            className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
            <Plus size={14} className="mr-1" /> Add Company
          </Button>
        </div>
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="inhouse-companies-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">#</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Company Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c, i) => (
                <TableRow key={c.id} className="border-border hover:bg-muted/50" data-testid={`inhouse-company-${c.id}`}>
                  <TableCell className="w-12 font-mono text-xs text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="text-sm font-semibold">{c.name}</TableCell>
                </TableRow>
              ))}
              {!companies.length && (
                <TableRow><TableCell colSpan={2} className="py-10 text-center text-sm text-muted-foreground">No companies registered.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
            <Wrench size={24} className="text-primary" /> Workshop Partner Accounts
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            External workshop portals — separate from internal staff. On creation, a temporary password is auto-generated and emailed to the partner with login instructions.
          </p>
        </div>
        <form onSubmit={createWorkshop} className="grid grid-cols-1 gap-4 rounded-sm border border-border bg-card p-6 md:grid-cols-3" data-testid="workshop-create-form">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Workshop Name *</Label>
            <Input data-testid="workshop-name-input" required value={wForm.name} onChange={(e) => setWForm({ ...wForm, name: e.target.value })}
              placeholder="e.g. Speedy Auto Works" className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email *</Label>
            <Input data-testid="workshop-email-input" type="email" required value={wForm.email} onChange={(e) => setWForm({ ...wForm, email: e.target.value })}
              placeholder="workshop@example.com" className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Phone Number *</Label>
            <Input data-testid="workshop-phone-input" required value={wForm.phone} onChange={(e) => setWForm({ ...wForm, phone: e.target.value })}
              placeholder="012-345 6789" className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="md:col-span-3">
            <Button data-testid="workshop-create-button" type="submit" disabled={wBusy}
              className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              <Plus size={14} className="mr-1" /> {wBusy ? "Creating & Emailing..." : "Create & Email Credentials"}
            </Button>
          </div>
        </form>
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="workshop-accounts-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Workshop</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Phone</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Created</TableHead>
                {canReset && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {workshops.map((w) => (
                <TableRow key={w.id} className="border-border hover:bg-muted/50" data-testid={`workshop-row-${w.id}`}>
                  <TableCell className="text-sm font-semibold">{w.name}</TableCell>
                  <TableCell className="font-mono text-xs">{w.email}</TableCell>
                  <TableCell className="font-mono text-xs">{w.phone}</TableCell>
                  <TableCell>
                    {w.is_active !== false
                      ? <Badge variant="outline" className="rounded-sm border-emerald-500/40 text-[10px] font-bold uppercase text-emerald-400">Active</Badge>
                      : <Badge variant="outline" className="rounded-sm border-red-500/40 text-[10px] font-bold uppercase text-red-400">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{(w.created_at || "").slice(0, 10)}</TableCell>
                  {canReset && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" data-testid={`workshop-reset-${w.id}`} onClick={() => resetWorkshopPw(w)}
                        className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                        <Key size={14} className="mr-1" /> Reset Password
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!workshops.length && (
                <TableRow><TableCell colSpan={canReset ? 6 : 5} className="py-10 text-center text-sm text-muted-foreground">No workshop partner accounts yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
